import { useEffect, useRef, useState } from 'react';

/**
 * A loading percentage that always counts upward, one number at a time.
 *
 * Every waiting screen in the app renders a bar from this hook, so the count is
 * the same motion everywhere rather than something each screen invents.
 *
 * The previous bar was driven straight off a caller's fraction, and the only
 * caller that had one derived it from four sections resolving — so it rendered
 * 0, 25, 50, 75, 100 and read as a bar teleporting rather than filling. The
 * number here is the same information, but approached instead of jumped to: a
 * real signal moves the TARGET, and the displayed value walks toward it.
 *
 * Speed is proportional to the distance left (`dp/dt = k · (target − p)`), which
 * gives the shape people expect from a loader — quick off the mark, easing as it
 * closes. A slow screen therefore ticks up literally one at a time, while a fast
 * one covers ground in bigger steps. That is the intended trade: the brief is
 * "count to 100 by one, it can jump based on the speed of loading", and pinning
 * the rate low enough to never skip an integer would put a hard ~1.7s floor
 * (100 frames at 60fps) under every screen transition in the app.
 *
 * Accumulation is fractional and only the floor is published, so the value never
 * goes backwards and never skips 100.
 */

/** Distance-proportional rate while waiting. Tuned against MIN_VISIBLE_MS (300ms). */
const CLIMB_K = 2.2;
/** Faster pull once the wait is over, so the finish lands inside the fade-out. */
const FINISH_K = 9;
/** Floor on the rate, so a long wait keeps ticking instead of appearing frozen. */
const MIN_RATE = 6;
/**
 * Floor on the rate once the wait is over, in percent per second.
 *
 * FINISH_K alone cannot finish: a distance-proportional rate approaches its
 * target asymptotically, so the count lands on 95 and unmounts with the fade
 * rather than reaching 100. This floor is what closes the last stretch.
 *
 * 260%/s clears the widest realistic gap — a handoff at ~45, which is where a
 * 300ms MIN_VISIBLE_MS wait leaves it — in about 210ms, inside the 250ms
 * transition fade, and far inside the 800ms boot fade.
 */
const MIN_FINISH_RATE = 260;
/**
 * How high the count climbs on its own with no real signal to follow.
 *
 * Short of 100 on purpose: arriving at 100 while the screen is still working
 * would be the bar lying, and the user then watches a finished bar sit there.
 */
const CREEP_CEILING = 92;

export interface ProgressCounterOptions {
  /**
   * 0..1 when the caller genuinely knows how far along it is, omitted when it
   * does not. A real value steers the target; it is never rendered directly.
   */
  realProgress?: number | null;
  /** Snap instead of animate. */
  reducedMotion?: boolean;
}

/**
 * @param active true while the screen is waiting. Going false finishes the
 *               count at 100 rather than cutting it off wherever it had got to.
 * @returns an integer 0..100.
 */
export function useProgressCounter(
  active: boolean,
  { realProgress = null, reducedMotion = false }: ProgressCounterOptions = {},
): number {
  const [pct, setPct] = useState(0);

  // The precise value. `pct` is its floor, so rendering cannot drift the maths.
  const exactRef = useRef(0);
  const realRef = useRef(realProgress);
  const activeRef = useRef(active);

  realRef.current = realProgress;
  activeRef.current = active;

  // A fresh wait starts from zero. Done in a layout-free effect keyed on the
  // transition into `active` so a re-render mid-wait does not restart the count.
  const wasActiveRef = useRef(false);
  useEffect(() => {
    if (active && !wasActiveRef.current) {
      exactRef.current = 0;
      setPct(0);
    }
    wasActiveRef.current = active;
  }, [active]);

  useEffect(() => {
    if (reducedMotion) {
      const snapped = active
        ? Math.round((realProgress ?? 0) * 100)
        : 100;
      exactRef.current = snapped;
      setPct(snapped);
      return;
    }

    let frame = 0;
    let last = performance.now();

    const step = (now: number) => {
      // Clamped: a backgrounded tab resumes with a huge delta, which would jump
      // the bar to its target in a single frame the moment it returns.
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      const isActive = activeRef.current;
      const real = realRef.current;

      // A real fraction RAISES the ceiling; it never sets the value.
      //
      // Discover's fraction is four booleans over four, so it reads 0 until the
      // first section lands. Steering the target with it directly would park
      // the one screen that can measure itself at 0 while every screen that
      // cannot climbs happily — the opposite of the point. Taking the larger of
      // the two means the climb always moves, and a caller further along than
      // the default ceiling can still push past it.
      const realPct = typeof real === 'number'
        ? Math.max(0, Math.min(100, real * 100))
        : 0;
      const target = !isActive ? 100 : Math.max(CREEP_CEILING, realPct);

      const k = isActive ? CLIMB_K : FINISH_K;
      const remaining = target - exactRef.current;

      if (remaining > 0) {
        const floor = isActive ? MIN_RATE : MIN_FINISH_RATE;
        const rate = Math.max(floor, remaining * k);
        exactRef.current = Math.min(target, exactRef.current + rate * dt);
        const next = Math.floor(exactRef.current);
        setPct((prev) => (next > prev ? next : prev));
      }

      // Keep the loop alive while waiting, and after the wait until it lands on
      // 100 — dropping it early is what would leave a bar stopped at 73.
      if (activeRef.current || exactRef.current < 100) {
        frame = requestAnimationFrame(step);
      }
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
    // realProgress is read through a ref, so a new fraction does not restart the
    // loop — it just moves the target the running loop is already chasing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, reducedMotion]);

  return pct;
}
